/*
 * Mobile inline video autoplay
 *
 * Small screens get the same inline players as desktop: a video loads and
 * starts playing as it scrolls into view, and pauses again when it leaves.
 * Works with Shopify-hosted videos (<video>) and YouTube or Vimeo embeds
 * (<iframe>).
 *
 * Two browser rules shape this. iOS plays a <video> in its own fullscreen
 * player unless `playsinline` is set, and no browser will autoplay a video
 * that has sound. So autostarted playback is muted, with the player controls
 * left in place so the visitor can turn sound on; nothing here mutes a video
 * again once it has started, so unmuting sticks. A video the visitor starts
 * themselves is never touched, and so keeps its sound.
 *
 * Autoplay is skipped when the visitor prefers reduced motion or has Data
 * Saver turned on.
 *
 * Embeds take two further precautions. Commands are posted to a player that
 * may not have loaded yet, so they are repeated until it answers; otherwise a
 * pause sent while scrolling away is silently dropped and the video plays on.
 * And an iframe swallows touch events, so an autostarted embed ignores them
 * until it is tapped, letting a swipe across it move the gallery instead.
 *
 * Markup opts in with `data-mobile-autoplay` on a <deferred-media> element.
 */

(function () {
  const MOBILE_BREAKPOINT = '(max-width: 749px)';
  const VISIBLE_RATIO = 0.35;
  const YOUTUBE_PLAY = '{"event":"command","func":"playVideo","args":""}';
  const YOUTUBE_PAUSE = '{"event":"command","func":"pauseVideo","args":""}';
  const YOUTUBE_LISTEN = '{"event":"listening"}';
  const VIMEO_PLAY = '{"method":"play"}';
  const VIMEO_PAUSE = '{"method":"pause"}';
  const COMMAND_RETRY_MS = 250;
  const COMMAND_ATTEMPTS = 12;

  if (!('IntersectionObserver' in window)) return;

  // Several sections include this file, and each tag runs it again.
  if (window.mobileVideoAutoplayLoaded) return;
  window.mobileVideoAutoplayLoaded = true;

  const mobile = window.matchMedia(MOBILE_BREAKPOINT);
  const managed = new Set();
  const pausedWhileHidden = new Set();
  const players = new WeakMap();
  const embeds = new Set();
  let observer = null;
  let galleryObserver = null;

  function autoplayAllowed() {
    if (!mobile.matches) return false;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    return !(navigator.connection && navigator.connection.saveData);
  }

  function mediaContainers(root) {
    return (root || document).querySelectorAll('deferred-media[data-mobile-autoplay]');
  }

  function deferredMarkup(container) {
    const template = container.querySelector('template');
    return template ? template.content : null;
  }

  function visibleRatio(element) {
    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height) return 0;
    const height = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
    const width = Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0);
    if (height <= 0 || width <= 0) return 0;
    return (height * width) / (rect.width * rect.height);
  }

  /* Embedded players ------------------------------------------------------ */

  function post(iframe, message) {
    if (!iframe.contentWindow) return;
    iframe.contentWindow.postMessage(message, '*');
  }

  function commandsFor(iframe) {
    if (iframe.classList.contains('js-youtube')) return { play: YOUTUBE_PLAY, pause: YOUTUBE_PAUSE };
    if (iframe.classList.contains('js-vimeo')) return { play: VIMEO_PLAY, pause: VIMEO_PAUSE };
    return null;
  }

  // A player that has not finished loading drops whatever is posted to it, so
  // the command is repeated until the player talks back or the attempts run out.
  function send(iframe, action) {
    const commands = commandsFor(iframe);
    if (!commands) return;

    let player = players.get(iframe);
    if (!player) {
      player = { ready: false, pending: null, attempts: 0, timer: null };
      players.set(iframe, player);
      embeds.add(iframe);
      iframe.addEventListener('load', () => post(iframe, YOUTUBE_LISTEN));
      post(iframe, YOUTUBE_LISTEN);
    }

    player.pending = commands[action];
    player.attempts = 0;
    flush(iframe);
  }

  function flush(iframe) {
    const player = players.get(iframe);
    if (!player || !player.pending) return;

    post(iframe, player.pending);

    if (player.ready) {
      player.pending = null;
      return;
    }

    window.clearTimeout(player.timer);
    player.attempts += 1;
    if (player.attempts >= COMMAND_ATTEMPTS) {
      player.pending = null;
      return;
    }
    player.timer = window.setTimeout(() => flush(iframe), COMMAND_RETRY_MS);
  }

  window.addEventListener('message', (event) => {
    if (!/^https?:\/\/(www\.)?(youtube(-nocookie)?\.com|player\.vimeo\.com)/.test(event.origin)) return;
    embeds.forEach((iframe) => {
      if (iframe.contentWindow !== event.source) return;
      const player = players.get(iframe);
      if (!player) return;
      player.ready = true;
      if (player.pending) {
        post(iframe, player.pending);
        player.pending = null;
        window.clearTimeout(player.timer);
      }
    });
  });

  /* An iframe keeps every touch that lands on it, so a swipe across an
     autostarted embed would never reach the gallery. It stays untouchable
     until tapped, and goes back to that once playback stops. */
  function holdTouchesForGallery(container) {
    const iframe = container.querySelector('iframe');
    if (!iframe) return;
    iframe.style.pointerEvents = 'none';
    delete container.dataset.playerActivated;
  }

  function handOverTouches(container) {
    const iframe = container.querySelector('iframe');
    if (!iframe || container.dataset.playerActivated) return;
    iframe.style.pointerEvents = '';
    container.dataset.playerActivated = 'true';
  }

  /* Playback -------------------------------------------------------------- */

  // Keeps iOS from taking a Shopify-hosted video fullscreen. Embeds carry the
  // same instruction as a `playsinline` parameter on their URL.
  function allowInlinePlayback(container) {
    const markup = deferredMarkup(container);
    const video = markup && markup.querySelector('video');
    if (video) video.setAttribute('playsinline', 'playsinline');
  }

  // Mutes the markup while it is still in the template, so playback is already
  // silent the moment it loads and the browser lets it start.
  function muteBeforeLoad(container) {
    const markup = deferredMarkup(container);
    if (!markup) return;

    const video = markup.querySelector('video');
    if (video) {
      video.setAttribute('muted', 'muted');
      return;
    }

    const iframe = markup.querySelector('iframe');
    if (!iframe) return;

    const parameter = iframe.classList.contains('js-vimeo') ? 'muted=1' : 'mute=1';
    const source = iframe.getAttribute('src') || '';
    if (source.indexOf(parameter) !== -1) return;
    iframe.setAttribute('src', source + (source.indexOf('?') === -1 ? '?' : '&') + parameter);
  }

  function play(container) {
    const video = container.querySelector('video');
    if (video) {
      const playback = video.play();
      // A rejected play() just means the browser declined; the poster stays put.
      if (playback && typeof playback.catch === 'function') playback.catch(() => {});
      return;
    }

    const iframe = container.querySelector('iframe');
    if (iframe) send(iframe, 'play');
  }

  function pause(container) {
    const video = container.querySelector('video');
    if (video) {
      video.pause();
      return;
    }

    const iframe = container.querySelector('iframe');
    if (iframe) {
      send(iframe, 'pause');
      holdTouchesForGallery(container);
    }
  }

  function start(container) {
    if (!container.hasAttribute('loaded')) {
      if (typeof container.loadContent !== 'function') return;
      muteBeforeLoad(container);
      container.loadContent(false);
      managed.add(container);

      const video = container.querySelector('video');
      if (video) video.muted = true;
      holdTouchesForGallery(container);
    } else if (!managed.has(container)) {
      // The visitor started this one, so leave its sound and state alone.
      return;
    }

    play(container);
  }

  function onIntersection(entries) {
    entries.forEach((entry) => {
      const container = entry.target;

      if (!entry.isIntersecting || entry.intersectionRatio < VISIBLE_RATIO) {
        if (managed.has(container)) pause(container);
        return;
      }

      if (!autoplayAllowed()) return;
      start(container);
    });
  }

  /* Wiring ---------------------------------------------------------------- */

  function observe(root) {
    mediaContainers(root).forEach((container) => {
      allowInlinePlayback(container);
      if (!container.dataset.mobileAutoplayBound) {
        container.dataset.mobileAutoplayBound = 'true';
        container.addEventListener('click', () => handOverTouches(container));
      }
      observer.observe(container);
    });
  }

  // Variant changes swap gallery markup, so forget what has left the document
  // and pick up whatever replaced it.
  function prune() {
    managed.forEach((container) => {
      if (!container.isConnected) managed.delete(container);
    });
    pausedWhileHidden.forEach((container) => {
      if (!container.isConnected) pausedWhileHidden.delete(container);
    });
  }

  function refresh(root) {
    if (!observer) return;
    prune();
    observe(root);
  }

  function watchGalleries() {
    if (!observer || !('MutationObserver' in window)) return;
    if (galleryObserver) galleryObserver.disconnect();
    galleryObserver = new MutationObserver(() => refresh());
    document.querySelectorAll('media-gallery').forEach((gallery) => {
      galleryObserver.observe(gallery, { childList: true, subtree: true });
    });
  }

  function sync() {
    if (!mobile.matches) {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      if (galleryObserver) {
        galleryObserver.disconnect();
        galleryObserver = null;
      }
      return;
    }

    if (!observer) observer = new IntersectionObserver(onIntersection, { threshold: [0, VISIBLE_RATIO] });
    observe(document);
    watchGalleries();
  }

  sync();

  if (mobile.addEventListener) mobile.addEventListener('change', sync);

  document.addEventListener('shopify:section:load', (event) => {
    refresh(event.target);
    watchGalleries();
  });

  // Nothing should keep playing into a tab the visitor has left.
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      managed.forEach((container) => {
        if (visibleRatio(container) >= VISIBLE_RATIO) pausedWhileHidden.add(container);
        pause(container);
      });
      return;
    }

    pausedWhileHidden.forEach((container) => {
      if (container.isConnected && autoplayAllowed() && visibleRatio(container) >= VISIBLE_RATIO) play(container);
    });
    pausedWhileHidden.clear();
  });
})();
